import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Users, Clock, Loader2, ArrowRight, ArrowLeft, Settings, X, Activity, Star } from "lucide-react";
import { usePublicActivities } from "@/hooks/usePublicActivities";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { ActivityConfigModal } from "./ActivityConfigModal";

interface Activity {
  id: string;
  name: string;
  schedule?: string;
  location?: string;
  description?: string;
  current_enrollment?: number;
  max_capacity?: number;
  requires_day_selection: boolean;
  start_time?: string;
  end_time?: string;
}

interface ActivityConfig {
  id: string;
  name: string;
  billingType: 'per_session' | 'monthly';
  selectedDays: number[];
  requiresDaySelection: boolean;
}

interface ActivitySelectionStepProps {
  activityConfigs: ActivityConfig[];
  onActivityAdd: (config: ActivityConfig) => void;
  onActivityRemove: (activityId: string) => void;
  onNext: () => void;
  onBack: () => void;
}

// Day names mapping: day value -> name (1=Lunes, 2=Martes, 3=Miércoles, 4=Jueves, 5=Viernes)
const dayNames: { [key: number]: string } = {
  1: 'Lunes',
  2: 'Martes', 
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes'
};

export function ActivitySelectionStep({ 
  activityConfigs, 
  onActivityAdd, 
  onActivityRemove, 
  onNext, 
  onBack 
}: ActivitySelectionStepProps) {
  const { activities, loading: activitiesLoading } = usePublicActivities();
  const { toast } = useToast();
  const [currentConfigActivity, setCurrentConfigActivity] = useState<Activity | null>(null);



  const handleActivitySelect = (activity: Activity) => {
    setCurrentConfigActivity(activity);
  };

  const handleActivityConfig = (config: ActivityConfig) => {
    onActivityAdd(config);
    toast({
      title: "Actividad configurada",
      description: `${config.name} ha sido añadida a tu selección`,
    });
  };

  const handleNext = () => {
    if (activityConfigs.length === 0) {
      toast({
        title: "Sin actividades",
        description: "Debes seleccionar al menos una actividad",
        variant: "destructive",
      });
      return;
    }
    onNext();
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6 lg:space-y-8 px-2 lg:px-0">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-lg">
          <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
        </div>
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Selecciona las Actividades</h2>
        <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto px-4">
          Elige las actividades extracurriculares que más te interesen
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-start">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="h-10 sm:h-12 px-4 sm:px-6 rounded-xl border-gray-200 hover:border-gray-300 transition-all text-sm sm:text-base"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          <span className="hidden sm:inline">Volver a datos personales</span>
          <span className="sm:hidden">Volver</span>
        </Button>
      </div>

      {/* Activities Grid */}
      <div className="space-y-6">
        {activitiesLoading ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl mb-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
            <p className="text-lg text-gray-600">Cargando actividades disponibles...</p>
          </div>
        ) : !activities || activities.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl mb-4">
              <Activity className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-lg text-gray-600">No hay actividades disponibles</p>
            <p className="text-sm text-gray-500 mt-2">
              {activities === null ? 'Error cargando datos' : 'No se encontraron actividades'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            {activities.map((activity) => {
              const isAlreadySelected = activityConfigs.some(config => config.id === activity.id);
              const currentEnrollment = activity.current_enrollment || 0;
              const maxCapacity = activity.max_capacity || 0;
              const isAtCapacity = currentEnrollment >= maxCapacity;

              return (
                <Card 
                  key={activity.id} 
                  className={cn(
                    "group cursor-pointer transition-all duration-300 border-0 shadow-lg",
                    "lg:hover:shadow-2xl lg:hover:scale-[1.02]",
                    isAlreadySelected 
                      ? "bg-gradient-to-br from-green-50 to-emerald-50 ring-2 ring-green-500/20 shadow-green-500/10" 
                      : "bg-gradient-to-br from-white to-gray-50/30 lg:hover:shadow-blue-500/10",
                    isAtCapacity && !isAlreadySelected && "opacity-75"
                  )}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={cn(
                          "w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center transition-all duration-300",
                          isAlreadySelected 
                            ? "bg-green-500 text-white shadow-lg shadow-green-500/30" 
                            : "bg-blue-100 text-blue-600 lg:group-hover:bg-blue-500 lg:group-hover:text-white"
                        )}>
                          {isAlreadySelected ? (
                            <CheckCircle className="w-5 h-5 lg:w-6 lg:h-6" />
                          ) : (
                            <Star className="w-5 h-5 lg:w-6 lg:h-6" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-lg lg:text-xl text-gray-900 lg:group-hover:text-blue-900 transition-colors truncate">
                            {activity.name}
                          </CardTitle>
                        </div>
                      </div>
                      {isAlreadySelected && (
                        <div className="animate-bounce">
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-3 lg:space-y-4">
                    {/* Activity Details */}
                    <div className="space-y-2 lg:space-y-3">
                      <div className="flex items-center gap-2 lg:gap-3 text-gray-600">
                        <div className="w-6 h-6 lg:w-8 lg:h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Clock className="w-3 h-3 lg:w-4 lg:h-4" />
                        </div>
                        <span className="text-xs lg:text-sm font-medium leading-tight">
                          {activity.schedule || 'Horario por confirmar'}
                        </span>
                      </div>
                      
                      
                      <div className="flex items-center gap-2 lg:gap-3 text-gray-600">
                        <div className="w-6 h-6 lg:w-8 lg:h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Users className="w-3 h-3 lg:w-4 lg:h-4" />
                        </div>
                        <span className="text-xs lg:text-sm font-medium leading-tight">
                          {maxCapacity > 0 ? `${currentEnrollment}/${maxCapacity} alumnos` : 'Sin límite de plazas'}
                        </span>
                      </div>
                    </div>

                    {activity.description && (
                      <CardDescription className="text-sm leading-relaxed text-gray-600 bg-gray-50/50 p-3 rounded-xl">
                        {activity.description}
                      </CardDescription>
                    )}

                    {/* Action Buttons */}
                    <div className="pt-2 space-y-2">
                      {isAlreadySelected ? (
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onActivityRemove(activity.id)}
                            className="h-10 lg:h-11 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-xl transition-all text-xs lg:text-sm"
                          >
                            <X className="mr-1 lg:mr-2 h-3 w-3 lg:h-4 lg:w-4" />
                            Quitar
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleActivitySelect(activity)}
                            className="h-10 lg:h-11 border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 rounded-xl transition-all text-xs lg:text-sm"
                          >
                            <Settings className="mr-1 lg:mr-2 h-3 w-3 lg:h-4 lg:w-4" />
                            Configurar
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          onClick={() => handleActivitySelect(activity)}
                          disabled={isAtCapacity}
                          className={cn(
                            "w-full h-10 lg:h-12 rounded-xl font-semibold transition-all duration-300 text-sm lg:text-base",
                            "lg:transform lg:hover:scale-105",
                            isAtCapacity 
                              ? "bg-gray-300 hover:bg-gray-300 cursor-not-allowed"
                              : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg lg:hover:shadow-xl"
                          )}
                        >
                          {isAtCapacity ? (
                            <>
                              <X className="mr-2 h-3 w-3 lg:h-4 lg:w-4" />
                              Sin plazas
                            </>
                          ) : (
                            <>
                              <Star className="mr-2 h-3 w-3 lg:h-4 lg:w-4" />
                              <span className="hidden lg:inline">Seleccionar actividad</span>
                              <span className="lg:hidden">Seleccionar</span>
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected Activities Summary */}
      {activityConfigs.length > 0 && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-full border border-green-200">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-sm font-semibold text-green-700">
                {activityConfigs.length} actividad{activityConfigs.length > 1 ? 'es' : ''} seleccionada{activityConfigs.length > 1 ? 's' : ''}
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            {activityConfigs.map((config) => (
              <Card key={config.id} className="border-0 bg-gradient-to-r from-green-50 to-emerald-50 shadow-lg">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-white" />
                      </div>
                      <div className="space-y-1">
                        <span className="font-semibold text-base sm:text-lg text-gray-900">{config.name}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-white/80 border-green-200 text-green-700">
                            {config.billingType === 'per_session' ? 'Uso días sueltos' : 'Uso mensual'}
                          </Badge>
                          {config.requiresDaySelection && config.selectedDays.length > 0 && (
                            <div className="flex gap-1">
                              {config.selectedDays.map((day) => (
                                <Badge key={day} variant="outline" className="text-xs bg-white/80 border-blue-200 text-blue-700">
                                  {dayNames[day]}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onActivityRemove(config.id)}
                      className="w-10 h-10 rounded-xl text-red-500 hover:bg-red-100 hover:text-red-600"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="flex justify-center pt-4">
            <Button 
              onClick={handleNext} 
              className="h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg font-semibold bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
            >
              <span className="hidden sm:inline">Continuar al resumen</span>
              <span className="sm:hidden">Continuar</span>
              <ArrowRight className="ml-2 sm:ml-3 h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
          </div>
        </div>
      )}

      <ActivityConfigModal
        activity={currentConfigActivity}
        isOpen={!!currentConfigActivity}
        onClose={() => setCurrentConfigActivity(null)}
        onConfirm={handleActivityConfig}
      />
    </div>
  );
}